// Fullscreen preview mode: hides the panels so the tiling fills the
// browser undisturbed. Nav controls appear while the cursor moves and
// fade out after a moment of stillness. Mouse wheel zooms the tiling.

import { onZoom } from './tilesize.js';
import { setBackground, isBackgroundTransitioning } from './background.js';
import { openPromptModal, isPromptModalOpen, closePromptModal } from './promptmodal.js';
import { openReportModal, isReportModalOpen, closeReportModal } from './reportmodal.js';
import { downloadTile } from './download.js';

const overlay = document.getElementById('preview-overlay');
const prevBtn = document.getElementById('prev-tile');
const nextBtn = document.getElementById('next-tile');
const closeBtn = document.getElementById('close-preview');
const promptBtn = document.getElementById('prompt-tile');
const heartBtn = document.getElementById('heart-tile');
const heartCount = heartBtn.querySelector('span');
const downloadBtn = document.getElementById('download-tile');
const reportBtn = document.getElementById('report-tile');
const removeBtn = document.getElementById('remove-tile');
const captionEl = document.getElementById('preview-caption');

// Items carry their own capabilities: `heart` ({favorites, favorited,
// toggle}) and `onRemove` (async, true when the tile was removed) appear as
// buttons only on items that brought them. On touch screens the thumbnails
// carry no overlay controls at all, so this is where those actions live.
let items = []; // [{ src, label, prompt?, seed?, heart?, onRemove?, removeLabel? }]
let index = 0;
let active = false;
let hideTimer = null;
let onExit = null;

function paintHeart(item) {
  heartBtn.classList.toggle('hidden', !item.heart);
  if (!item.heart) return;
  heartCount.textContent = item.heart.favorites;
  heartBtn.classList.toggle('on', !!item.heart.favorited);
  heartBtn.disabled = !item.heart.toggle;
  const title = item.heart.toggle ? 'Love this tile' : 'Sign in with Sogni to vote';
  heartBtn.title = title;
  heartBtn.setAttribute('aria-label', title);
}

function caption() {
  const item = items[index];
  const text = items.length > 1 ? `${item.label} · ${index + 1}/${items.length}` : item.label;
  captionEl.textContent = text;
  // A caption with nothing to say (a bare background with no record) is
  // chrome for its own sake; the pill only appears when it informs.
  captionEl.classList.toggle('hidden', !text);
  promptBtn.classList.toggle('hidden', !item.prompt);
  downloadBtn.classList.toggle('hidden', !item.src);
  paintHeart(item);
  // On touch the thumbnails carry no overlay controls at all, so this is the
  // only way in to a report there. Items opt in by carrying a `report`.
  reportBtn.classList.toggle('hidden', !item.report);
  if (item.report) {
    const done = !!item.report.done;
    reportBtn.disabled = done;
    const label = done ? 'Reported' : 'Report this tile';
    reportBtn.title = label;
    reportBtn.setAttribute('aria-label', label);
  }
  removeBtn.classList.toggle('hidden', !item.onRemove);
  if (item.onRemove) {
    const label = item.removeLabel || 'Remove';
    removeBtn.title = label;
    removeBtn.setAttribute('aria-label', label);
  }
  // Arrows that cannot go anywhere read as broken. With one item there is
  // nothing to step to, so they are removed rather than left inert.
  const single = items.length < 2;
  prevBtn.classList.toggle('hidden', single);
  nextBtn.classList.toggle('hidden', single);
}

function wake() {
  overlay.classList.add('show');
  clearTimeout(hideTimer);
  hideTimer = setTimeout(() => overlay.classList.remove('show'), 2400);
}

function step(delta) {
  // Let the crossfade finish rather than cutting it short — a rapid
  // second press would otherwise skip a tile entirely.
  if (isBackgroundTransitioning() || items.length < 2) {
    wake();
    return;
  }
  index = (index + delta + items.length) % items.length;
  setBackground(items[index].src);
  caption();
  wake();
}

export function enterPreview(list, startIndex, exitCallback) {
  if (!list.length) return;
  items = list;
  index = Math.max(0, Math.min(startIndex, list.length - 1));
  onExit = exitCallback;
  active = true;
  document.body.classList.add('preview');
  setBackground(items[index].src);
  caption();
  wake();
}

export function exitPreview() {
  if (!active) return;
  active = false;
  closePromptModal();
  closeReportModal();
  document.body.classList.remove('preview');
  overlay.classList.remove('show');
  clearTimeout(hideTimer);
  if (onExit) onExit();
}

prevBtn.onclick = (e) => {
  e.stopPropagation();
  step(-1);
};
nextBtn.onclick = (e) => {
  e.stopPropagation();
  step(1);
};
closeBtn.onclick = (e) => {
  e.stopPropagation();
  exitPreview();
};
promptBtn.onclick = (e) => {
  e.stopPropagation();
  openPromptModal(items[index]);
};

downloadBtn.onclick = (e) => {
  e.stopPropagation();
  const item = items[index];
  downloadTile(item.src, item.name || item.label, item.seed);
  wake();
};

reportBtn.onclick = (e) => {
  e.stopPropagation();
  const item = items[index];
  if (!item.report || item.report.done) return;
  openReportModal({
    ...item.report,
    name: item.name || item.label,
    onSent: () => {
      item.report.done = true;
      item.report.onSent?.();
      caption();
    }
  });
  wake();
};

heartBtn.onclick = async (e) => {
  e.stopPropagation();
  const item = items[index];
  if (!item.heart?.toggle) return;
  try {
    const updated = await item.heart.toggle();
    if (updated) {
      item.heart.favorites = updated.favorites;
      item.heart.favorited = updated.favorited;
    }
  } catch (err) {
    console.warn(err.message);
  }
  paintHeart(item);
  wake();
};

removeBtn.onclick = async (e) => {
  e.stopPropagation();
  const item = items[index];
  if (!item.onRemove) return;
  let removed = false;
  try {
    removed = await item.onRemove();
  } catch (err) {
    console.warn(err.message);
  }
  if (!removed) {
    wake();
    return;
  }
  items.splice(index, 1);
  if (!items.length) {
    exitPreview();
    return;
  }
  index = index % items.length;
  setBackground(items[index].src);
  caption();
  wake();
};

document.addEventListener('mousemove', () => {
  if (active) wake();
});

// Touch devices never fire mousemove, so the controls faded after 2.4s and
// nothing could bring them back — they were still on screen at opacity 0 with
// pointer-events off, which read as "the buttons do nothing". A tap now wakes
// them, the same two-tap idiom media players use.
document.addEventListener(
  'pointerdown',
  (e) => {
    if (!active || isPromptModalOpen() || isReportModalOpen()) return;
    if (e.pointerType === 'mouse') return; // desktop already wakes on move
    wake();
  },
  { passive: true }
);

document.addEventListener('keydown', (e) => {
  if (!active) return;
  if (isPromptModalOpen()) {
    // The prompt inspector owns the keyboard while it is open.
    if (e.key === 'Escape') closePromptModal();
    return;
  }
  if (isReportModalOpen()) {
    // Likewise the report form — Escape closes it, not the preview behind it.
    if (e.key === 'Escape') closeReportModal();
    return;
  }
  if (e.key === 'Escape') exitPreview();
  else if (e.key === 'ArrowLeft') step(-1);
  else if (e.key === 'ArrowRight') step(1);
});

// Zooming is owned by tilesize.js (it works over the background everywhere,
// not just here); in preview the caption doubles as the live size readout.
onZoom((px, phase) => {
  if (!active) return;
  if (phase === 'move') {
    captionEl.textContent = `${px} px`;
    overlay.classList.add('show');
    // Hold the controls up for the whole gesture, not just until an
    // earlier stillness timer fires.
    clearTimeout(hideTimer);
  } else {
    caption();
    wake();
  }
});
